import React, { FC, SyntheticEvent, useRef, useState } from 'react';
import { Dialog } from 'ui-components/dialog/dialog';
import { DownloadIcon, GrayOfflineReportViewMapIcon } from 'components/assets/Icons/Icons';
import styles from './report-materials.module.scss';
import { Button } from 'ui-components/button/button';
import { windowOpen } from 'features/utils';
import { AttachmentItem, OrderManagementRequest } from 'core/entities';
import classnames from 'classnames';
import classNames from 'classnames';
import { RecordType } from 'features/offline-reports/order-management/components/requests-table/requests-table';
import { Loading } from 'ui-components/loading/loading-component';
import { API } from 'API';
import { downloadFile } from 'API/common/download-file-service/download-file-service';
import {
    getContentTypeHeader,
    getFilesList,
    getReportFileName,
} from 'features/offline-reports/order-management/components/utils/utils';
import { Tooltip } from 'ui-components/tooltip/tooltip';

const myZonePath = '/#!/admin/myzone/';
const myRequestsTableauPath = 'my-requests/placer-xtra/tableau/';

export const ReportMaterialsComponent: FC<{
    record: RecordType | OrderManagementRequest;
    viewReportButtonText?: string;
    downloadButtonText?: string;
}> = ({ record, viewReportButtonText, downloadButtonText }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { zipFile, mapsLink, tablouLink, id } = record;
    const downloadingCustomText = 'Downloading...';
    const isDownload = useRef<boolean>(false);

    const handleReportMaterialsTableauButton = (e: SyntheticEvent<EventTarget>, path: string) => {
        e.stopPropagation();
        path && windowOpen({ redirectionPath: path });
    };

    const handleReportMaterialsDownloadButton = (e: SyntheticEvent<EventTarget>) => {
        e.stopPropagation();
        const filedList = getFilesList(record.comments);
        const contentTypeHeader = getContentTypeHeader(filedList);
        const reportDownloadFileName = getReportFileName(record, contentTypeHeader, filedList);
        setIsDownloading(true);
        isDownload.current = true;
        downloadZIP(
            filedList,
            reportDownloadFileName.replace(' ', '_'),
            record.id,
            contentTypeHeader,
        );
    };

    const handleReportMaterialsOnlineButton = (e: SyntheticEvent<EventTarget>, path: string) => {
        e.stopPropagation();
        path && windowOpen({ redirectionPath: path });
    };

    const onCancel = (e: SyntheticEvent<EventTarget>) => {
        e.stopPropagation();

        isDownload.current = false;
        setIsDownloading(false);
    };

    const downloadZIP = async (
        filesList: AttachmentItem[],
        zipFileName: string,
        id: number,
        contentTypeHeader: string,
    ) => {
        try {
            const zip = await API.support.getRequestMaterialZip(id, filesList, contentTypeHeader);

            isDownload.current && zip.status === 200 && (await downloadFile(zip, zipFileName));
            setIsDownloading(false);
        } catch (e) {
            setIsDownloading(false);
        }
    };

    return (
        <div className={styles.reportMaterialsButtonsWrapper}>
            {zipFile && (
                <div className={styles.downloadButtonWrapper}>
                    <Tooltip placement="top" title={<div>Download files</div>}>
                        <Button
                            className={classNames(styles.button, styles.downloadSampleButton)}
                            onClick={(e) => handleReportMaterialsDownloadButton(e)}
                            icon={<DownloadIcon className={styles.buttonIcon} />}
                            data-testid="download-materials-button"
                            aria-label="download-report"
                        >
                            {downloadButtonText}
                        </Button>
                    </Tooltip>
                </div>
            )}
            {mapsLink && (
                <div className={styles.downloadButtonWrapper}>
                    <Tooltip placement="top" title={<div>View report</div>}>
                        <Button
                            className={classNames(styles.button, styles.viewReportButton)}
                            onClick={(e) => handleReportMaterialsOnlineButton(e, mapsLink)}
                            icon={<GrayOfflineReportViewMapIcon className={styles.buttonIcon} />}
                            data-testid="online-report-view-button"
                            aria-label="online-report"
                        />
                    </Tooltip>
                </div>
            )}
            {tablouLink && (
                <div className={styles.downloadButtonWrapper}>
                    <Tooltip placement="top" title={<div>View report</div>}>
                        <Button
                            className={classNames(styles.button, styles.viewReportButton)}
                            onClick={(e) =>
                                handleReportMaterialsTableauButton(
                                    e,
                                    `${myZonePath}${myRequestsTableauPath}${id}`,
                                )
                            }
                            icon={<GrayOfflineReportViewMapIcon className={styles.buttonIcon} />}
                            data-testid="tableau-report-view-button"
                            aria-label="tableau-report"
                        >
                            {viewReportButtonText}
                        </Button>
                    </Tooltip>
                </div>
            )}
            {isDownloading && (
                <div onClick={(e) => e.stopPropagation()}>
                    <Dialog
                        closable
                        maskClosable={false}
                        visible
                        onCancel={(e) => {
                            onCancel(e);
                        }}
                        className={classnames(styles.modal, styles.modalWithContent)}
                        centered
                        destroyOnClose
                        width={450}
                        footer={null}
                        bodyStyle={{
                            padding: '32px 30px 40px',
                        }}
                    >
                        <Loading show={isDownloading} customText={downloadingCustomText}></Loading>
                        <div className={styles.cancelButtonWrapper}>
                            <Button
                                onClick={(e) => {
                                    onCancel(e);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Dialog>
                </div>
            )}
        </div>
    );
};
